import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { truncate } from '@common/util/string';
import { ThemeService } from '@portal-core/general/services/theme.service';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { BarController, BarElement, CategoryScale, Chart, ChartData, ChartDataset, ChartOptions, Colors, LinearScale, Tooltip } from 'chart.js';
import { Observable, of, switchMap } from 'rxjs';

Chart.register(BarController, BarElement, CategoryScale, Colors, LinearScale, Tooltip);

interface ProjectChecklistChartColsData {
  Percents: number[];
  Labels: string[];
}

@Component({
  selector: 'mc-project-checklist-bar-chart',
  templateUrl: './project-checklist-bar-chart.component.html',
  styleUrls: ['./project-checklist-bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ProjectChecklistBarChartComponent implements OnInit {
  @Input() checklist: ProjectChecklist;
  @InputObservable('checklist') checklist$: Observable<ProjectChecklist>;

  chartColsData: ProjectChecklistChartColsData;
  chartColumnPaletteNames: string[] = ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5', 'chart-6'];
  chartData$: Observable<ChartData>;
  chartOptions: ChartOptions<'bar'> = {
    plugins: {
      tooltip: { enabled: true }
    },
    scales: {
      x: {
        ticks: {
          callback(value: number): string {
            return truncate(this.getLabelForValue(value), 14);
          }
        },
      },
      y: {
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };
  colAmounts: number = 0;

  constructor(private themeService: ThemeService) { }

  ngOnInit() {
    this.chartData$ = this.checklist$.pipe(
      switchMap((checklist) => {
        if (checklist) {
          this.chartColsData = this.parseChartData(this.checklist);
          this.colAmounts = this.chartColsData.Labels.length; // used to calculate how thick the chart's bars are.

          const datasets: ChartDataset<'bar'>[] = [{
            barPercentage: 0.5,
            barThickness: 50,
            maxBarThickness: 50,
            minBarLength: 2,
            data: this.chartColsData.Percents,
            backgroundColor: this.chartColsData.Labels.map((label, i) => this.themeService.getColor(this.chartColumnPaletteNames[i % this.chartColumnPaletteNames.length])),
          }];

          return of({
            datasets,
            labels: this.chartColsData.Labels
          });
        } else {
          this.chartColsData = null;
          this.colAmounts = null;
          return of(null);
        }
      })
    );
  }

  parseChartData(checklist: ProjectChecklist): ProjectChecklistChartColsData {
    const chartData = checklist.Analytics.ColumnFileCounts;
    const chartColsData: ProjectChecklistChartColsData = { Percents: [], Labels: [] };
    const indexes = Object.keys(chartData);

    let indexesWithOrder = indexes.map(index => {
      return { index, Order: chartData[index][0].Order };
    });

    indexesWithOrder = indexesWithOrder.sort((a, b) => a.Order > b.Order ? 1 : -1);

    for (let i = 0; i < indexesWithOrder.length; i++) {
      const index = indexesWithOrder[i].index;
      chartColsData.Labels.push(chartData[index][0].Legend);
      chartColsData.Percents.push(Math.round(chartData[index][0].Percent));
    }

    return chartColsData;
  }
}
